// components/MaskedDatePicker.tsx
import React, { forwardRef } from 'react';
import { DatePicker, DatePickerProps } from 'antd';
import dayjs from 'dayjs';
import {
  formatUSDatePretty,
  normalizeUSDatePretty,
  isValidUSDate,
} from '../../../utils/DateUtils';

const MaskedInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => {
  const { onKeyDown, onChange, onBlur, ...rest } = props;

  return (
    <input
      {...rest}
      ref={ref}
      onKeyDown={(e) => {
        const allowed =
          e.key === 'Backspace' ||
          e.key === 'Delete' ||
          e.key === 'Tab' ||
          e.key.startsWith('Arrow') ||
          /^[0-9]$/.test(e.key);
        if (!allowed) e.preventDefault();
        onKeyDown?.(e);
      }}
      onChange={(e) => {
        // auto-insert slashes progressively
        e.target.value = formatUSDatePretty(e.target.value);
        onChange?.(e); // let rc-picker receive the formatted text
      }}
      onBlur={(e) => {
        // normalize to MM/DD/YYYY (pads M/D)
        e.target.value = normalizeUSDatePretty(e.target.value);
        onBlur?.(e); // let rc-picker re-parse on blur
      }}
      inputMode="numeric"
      placeholder={rest.placeholder ?? 'MM/DD/YYYY'}
    />
  );
});
MaskedInput.displayName = 'MaskedInput';

export default function MaskedDatePicker(
  props: DatePickerProps & { errors?: any }
) {
  const { errors, ...rest } = props;
  return (
    <DatePicker
      {...rest}
      format="MM/DD/YYYY"
      className={`mt-1 w-full ${errors?.dateOfBirth ? 'ant-picker-error' : ''}`}
      style={{ height: 38, ...(props.style || {}) }}
      components={{ input: MaskedInput }}
      onChange={(d, str) => {
        if (
          typeof str === 'string' &&
          str.length === 10 &&
          isValidUSDate(str)
        ) {
          props.onChange?.(d ?? dayjs(str, 'MM/DD/YYYY'), str);
        } else if (!str) {
          props.onChange?.(null as unknown as dayjs.Dayjs, '');
        } else {
          // incomplete/invalid typing -> keep Dayjs null but pass the string along
          props.onChange?.(null as unknown as dayjs.Dayjs, str as any);
        }
      }}
    />
  );
}
